import React from 'react';
import { osc, wobble, lerp, loop, clamp, pulse, pathLerp, easeInOut } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES — Everyday body/physics concepts (Anivaze v2).
 * Each scene ACTs the concept out so a 5-year-old gets it.
 * Pure function of `t` (deterministic + exportable).
 * Reference style: see ConceptScenes.tsx / design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ━━━ Breathing: chest expands, blue air dots flow IN, grey dots flow OUT ━━━ */
export interface BreathingSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since scene started. Default 0 renders cleanly. */
  t?: number;
}

export const BreathingScene: React.FC<BreathingSceneProps> = ({
  opacity = 1,
  accentColor = '#38bdf8',
  t = 0,
  label = 'Breathe in fresh air, breathe out!',
}) => {
  // 4-second breathing cycle: 0–0.4 inhale, 0.4–0.6 hold, 0.6–1.0 exhale
  const cycle = loop(t, 4);              // 0→1 every 4 s
  const isInhale = cycle < 0.45;

  // Chest & belly scale: puffed on inhale, deflated on exhale
  const breathP = isInhale
    ? easeInOut(cycle / 0.45)
    : easeInOut(1 - (cycle - 0.45) / 0.55);
  const chestScaleY = lerp(1.0, 1.18, breathP);
  const chestScaleX = lerp(1.0, 1.08, breathP);

  // Lung fill: 0 = empty (grey), 1 = full (blue)
  const lungFill = breathP;
  const lungColor = `rgb(${Math.round(lerp(156, 56, lungFill))},${Math.round(lerp(163, 189, lungFill))},${Math.round(lerp(175, 248, lungFill))})`;

  // "In… Out…" label pulsing with the rhythm
  const labelPulse = osc(t, 4, 0.7, 1.0);

  // Air-dot flow (8 dots travelling the path)
  // Inhale path: nose → down into lungs   (downward arc)
  // Exhale path: lungs → up out of nose   (upward arc)
  const noseX = 400, noseY = 200;
  const lungLX = 330, lungLY = 305;
  const lungRX = 470, lungRY = 305;

  const inhalePath  = [
    { x: noseX, y: noseY - 10 },
    { x: noseX - 20, y: 255 },
    { x: lungLX, y: lungLY },
  ];
  const inhalePathR = [
    { x: noseX, y: noseY - 10 },
    { x: noseX + 20, y: 255 },
    { x: lungRX, y: lungRY },
  ];
  const exhalePath  = [
    { x: lungLX, y: lungLY },
    { x: noseX - 20, y: 255 },
    { x: noseX, y: noseY - 16 },
  ];
  const exhalePathR = [
    { x: lungRX, y: lungRY },
    { x: noseX + 20, y: 255 },
    { x: noseX, y: noseY - 16 },
  ];

  const dotCount = 6;
  const activePath  = isInhale ? inhalePath  : exhalePath;
  const activePathR = isInhale ? inhalePathR : exhalePathR;
  const dotColor = isInhale ? accentColor : '#9ca3af';
  const dotOpacityBase = breathP * 0.9;

  // Body centre
  const cx = 400, headY = 152;

  return (
    <g opacity={opacity}>
      {/* Sky-blue background */}
      <rect x={0} y={0} width={800} height={450} fill="#e0f7ff" />

      {/* Soft ground */}
      <ellipse cx={400} cy={430} rx={300} ry={40} fill="#b7e8f5" opacity={0.4} />

      {/* ── Head ── */}
      <ellipse cx={cx} cy={headY} rx={46} ry={52} fill="#fde68a" stroke="#f59e0b" strokeWidth={2.5} />
      {/* Eyes */}
      <circle cx={cx - 16} cy={headY - 8} r={6} fill="#fff" />
      <circle cx={cx + 16} cy={headY - 8} r={6} fill="#fff" />
      <circle cx={cx - 15} cy={headY - 7} r={3.5} fill="#1e3a5f" />
      <circle cx={cx + 17} cy={headY - 7} r={3.5} fill="#1e3a5f" />
      {/* Eyebrows (relaxed/friendly) */}
      <path d={`M${cx - 22},${headY - 17} Q${cx - 15},${headY - 22} ${cx - 9},${headY - 18}`}
        fill="none" stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
      <path d={`M${cx + 8},${headY - 18} Q${cx + 15},${headY - 22} ${cx + 22},${headY - 17}`}
        fill="none" stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
      {/* Nose */}
      <ellipse cx={cx} cy={headY + 6} rx={5} ry={3.5} fill="#f59e0b" />
      {/* Nostril highlight */}
      <circle cx={cx - 3} cy={headY + 7} r={1.8} fill="#b45309" />
      <circle cx={cx + 3} cy={headY + 7} r={1.8} fill="#b45309" />
      {/* Smile — grows wider on inhale */}
      <path
        d={`M${cx - 16},${headY + 22} Q${cx},${headY + 34 + breathP * 6} ${cx + 16},${headY + 22}`}
        fill="none" stroke="#b45309" strokeWidth={3} strokeLinecap="round"
      />
      {/* Cheek blush */}
      <ellipse cx={cx - 32} cy={headY + 10} rx={12} ry={7} fill="#fca5a5" opacity={0.5} />
      <ellipse cx={cx + 32} cy={headY + 10} rx={12} ry={7} fill="#fca5a5" opacity={0.5} />

      {/* ── Neck ── */}
      <rect x={cx - 14} y={headY + 48} width={28} height={22} rx={8} fill="#fde68a" stroke="#f59e0b" strokeWidth={2} />

      {/* ── Chest / torso (scales with breath) ── */}
      <g transform={`translate(${cx} 292) scale(${chestScaleX} ${chestScaleY}) translate(${-cx} -292)`}>
        {/* Torso body */}
        <rect x={cx - 68} y={270} width={136} height={110} rx={28} fill="#a5f3fc" stroke="#0284c7" strokeWidth={2.5} />
        {/* Shirt detail */}
        <rect x={cx - 20} y={272} width={40} height={6} rx={3} fill="#0284c7" opacity={0.25} />

        {/* ── Lungs (inside chest) ── */}
        {/* Left lung */}
        <ellipse cx={lungLX} cy={lungLY} rx={34} ry={44} fill={lungColor} stroke="#0369a1" strokeWidth={2} opacity={0.85} />
        {/* Right lung */}
        <ellipse cx={lungRX} cy={lungRY} rx={34} ry={44} fill={lungColor} stroke="#0369a1" strokeWidth={2} opacity={0.85} />
        {/* Bronchi (connecting tubes) */}
        <path d={`M${cx},${274} L${cx},${290} Q${cx - 10},${300} ${lungLX + 10},${lungLY - 30}`}
          fill="none" stroke="#0369a1" strokeWidth={5} strokeLinecap="round" />
        <path d={`M${cx},${290} Q${cx + 10},${300} ${lungRX - 10},${lungRY - 30}`}
          fill="none" stroke="#0369a1" strokeWidth={5} strokeLinecap="round" />
        {/* Heart (small, centre) */}
        <g transform={`translate(${cx} 300) scale(${1 + pulse(t, 0.8) * 0.08})`}>
          <path d="M0,-8 C-6,-16 -18,-14 -18,-4 C-18,4 0,16 0,16 C0,16 18,4 18,-4 C18,-14 6,-16 0,-8 Z"
            fill="#f87171" stroke="#b91c1c" strokeWidth={1.5} />
        </g>
      </g>

      {/* ── Shoulders & arms ── */}
      {/* Left arm */}
      <path
        d={`M${cx - 68},${282} Q${cx - 100},${310 + breathP * 6} ${cx - 88},${370}`}
        fill="none" stroke="#fde68a" strokeWidth={26} strokeLinecap="round"
      />
      <path
        d={`M${cx - 68},${282} Q${cx - 100},${310 + breathP * 6} ${cx - 88},${370}`}
        fill="none" stroke="#f59e0b" strokeWidth={2} strokeLinecap="round"
      />
      {/* Right arm */}
      <path
        d={`M${cx + 68},${282} Q${cx + 100},${310 + breathP * 6} ${cx + 88},${370}`}
        fill="none" stroke="#fde68a" strokeWidth={26} strokeLinecap="round"
      />
      <path
        d={`M${cx + 68},${282} Q${cx + 100},${310 + breathP * 6} ${cx + 88},${370}`}
        fill="none" stroke="#f59e0b" strokeWidth={2} strokeLinecap="round"
      />

      {/* ── Air dots flowing in / out ── */}
      {Array.from({ length: dotCount }, (_, i) => {
        const phase = (i / dotCount);
        const p = (loop(t, 2) + phase) % 1;   // staggered, loops every 2 s
        const pos  = pathLerp(activePath,  p);
        const posR = pathLerp(activePathR, p);
        const opacity2 = clamp(dotOpacityBase * (1 - Math.abs(p - 0.5) * 0.6), 0, 1);
        return (
          <g key={i}>
            <circle cx={pos.x}  cy={pos.y}  r={5} fill={dotColor} opacity={opacity2} />
            <circle cx={posR.x} cy={posR.y} r={5} fill={dotColor} opacity={opacity2} />
          </g>
        );
      })}

      {/* ── "In… Out…" rhythm label ── */}
      <g transform={`translate(400 195)`}>
        <rect x={-52} y={-18} width={104} height={30} rx={15}
          fill={isInhale ? '#bae6fd' : '#e5e7eb'}
          stroke={isInhale ? '#0284c7' : '#9ca3af'}
          strokeWidth={2} opacity={labelPulse}
        />
        <text x={0} y={6} textAnchor="middle"
          fill={isInhale ? '#0369a1' : '#4b5563'}
          fontSize={15} fontWeight={800} fontFamily="Inter,sans-serif"
          opacity={labelPulse}
        >
          {isInhale ? 'In…' : 'Out…'}
        </text>
      </g>

      {/* ── Floating sparkles (fresh air feel) ── */}
      {[
        { x: 120, y: 180, p: 0.1 },
        { x: 680, y: 200, p: 0.5 },
        { x: 90,  y: 310, p: 0.7 },
        { x: 710, y: 300, p: 0.3 },
      ].map((s, i) => {
        const scale = osc(t, 2.2, 0.6, 1.1, s.p * Math.PI * 2);
        return (
          <g key={i} transform={`translate(${s.x + wobble(t, 3.1 + i * 0.4, 5, s.p)},${s.y + wobble(t, 2.5 + i * 0.3, 4, s.p + 1)}) scale(${scale})`}>
            <circle r={6} fill="#bae6fd" opacity={0.7} />
            <circle r={3} fill="#fff" opacity={0.9} />
          </g>
        );
      })}

      {/* ── Caption ── */}
      <g transform="translate(400 424)">
        <rect x={-280} y={-20} width={560} height={36} rx={18}
          fill="rgba(255,255,255,0.92)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle"
          fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};

/* ━━━ Electricity: battery → wires → bulb → glows ━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface ElectricitySceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since scene started. Default 0 renders cleanly. */
  t?: number;
}

export const ElectricityScene: React.FC<ElectricitySceneProps> = ({
  opacity = 1,
  accentColor = '#fbbf24',
  t = 0,
  label = 'Electricity flows to light the bulb!',
}) => {
  // ── Circuit geometry (wire loop: battery → bulb → back) ──────────────────
  //
  //   Battery is left-centre.  Bulb is top-centre.  Switch is bottom-centre.
  //   Wire path (clockwise from battery +):
  //   A(battery top) → B(top-left) → C(bulb bottom) → D(top-right) → E(battery top)
  //   Bottom half: battery bottom → F(bottom-left) → G(switch) → H(bottom-right) → battery bottom
  //
  //   We concatenate both halves into one polyline so pathLerp works:
  //   A→B→C→D→A (top loop) folded into full circuit including bottom

  const batX = 160, batY = 225;   // battery centre
  const bulbX = 400, bulbY = 105; // bulb centre
  const swX = 400, swY = 360;     // switch centre

  // Wire waypoints (the electricity travels this loop)
  const wirePoints = [
    { x: batX,      y: batY - 38 },   // 0: battery + terminal (top)
    { x: batX,      y: 65  },          // 1: top-left corner
    { x: bulbX,     y: 65  },          // 2: top-centre (above bulb)
    { x: bulbX,     y: bulbY + 48 },   // 3: bulb bottom
    { x: 640,       y: 65  },          // 4: top-right corner
    { x: 640,       y: swY },          // 5: right side down to switch
    { x: swX + 54,  y: swY },          // 6: switch right
    { x: swX - 54,  y: swY },          // 7: switch left
    { x: batX,      y: swY },          // 8: bottom-left
    { x: batX,      y: batY + 38 },    // 9: battery − terminal (bottom)
    { x: batX,      y: batY - 38 },    // 10: back to start (loop close)
  ];

  // Number of electron dots on the wire
  const dotCount = 10;

  // Each dot moves along the wire loop continuously
  const dotElements = Array.from({ length: dotCount }, (_, i) => {
    const phase = i / dotCount;
    const p = (loop(t, 3.5) + phase) % 1;   // one lap every 3.5 s, staggered
    const pos = pathLerp(wirePoints, p);

    // Glow when near the bulb segment (waypoints 2–3)
    const bulbSegStart = 2 / (wirePoints.length - 1);
    const bulbSegEnd   = 3 / (wirePoints.length - 1);
    const nearBulb = p >= bulbSegStart && p <= bulbSegEnd;
    const dotGlow = nearBulb ? 1.0 : 0.75;
    const dotFill = nearBulb ? '#fff' : accentColor;

    return (
      <g key={i}>
        {nearBulb && <circle cx={pos.x} cy={pos.y} r={9} fill={accentColor} opacity={0.25} />}
        <circle cx={pos.x} cy={pos.y} r={4.5} fill={dotFill} opacity={dotGlow} />
      </g>
    );
  });

  // How bright is the bulb right now? — based on dots occupying the bulb segment
  const bulbPhase = loop(t, 3.5);
  const bulbSegStartFrac = 2 / (wirePoints.length - 1);
  const bulbSegEndFrac   = 3 / (wirePoints.length - 1);
  // Count fraction of dots near the bulb to drive glow
  const dotsNearBulb = Array.from({ length: dotCount }, (_, i) => {
    const p = (bulbPhase + i / dotCount) % 1;
    return (p >= bulbSegStartFrac && p <= bulbSegEndFrac) ? 1 : 0;
  }).reduce<number>((a, b) => a + b, 0);
  const glowStrength = clamp(dotsNearBulb / 3, 0, 1);
  // Bulb glow oscillates brightly when electricity arrives
  const bulbGlow   = lerp(0.3, 1.0, glowStrength) + osc(t, 0.45, 0, 0.08);
  const rayScale   = lerp(0.6, 1.3, glowStrength) + osc(t, 0.45, 0, 0.12);
  const bulbYellow = `rgba(253,224,71,${clamp(bulbGlow, 0, 1)})`;

  // Switch: jiggle hint
  const switchWobble = wobble(t, 3.8, 2, 0.5);

  return (
    <g opacity={opacity}>
      {/* Warm off-white background */}
      <rect x={0} y={0} width={800} height={450} fill="#fffbea" />

      {/* ── Wire loop (drawn first so components sit on top) ── */}
      {/* Top wire: battery → bulb → right */}
      <polyline
        points={wirePoints.slice(0, 5).map(p => `${p.x},${p.y}`).join(' ')}
        fill="none" stroke="#94a3b8" strokeWidth={7} strokeLinecap="round" strokeLinejoin="round"
      />
      {/* Right leg + switch */}
      <polyline
        points={wirePoints.slice(4, 10).map(p => `${p.x},${p.y}`).join(' ')}
        fill="none" stroke="#94a3b8" strokeWidth={7} strokeLinecap="round" strokeLinejoin="round"
      />
      {/* Bottom leg back to battery */}
      <line x1={batX} y1={swY} x2={batX} y2={batY + 38}
        stroke="#94a3b8" strokeWidth={7} strokeLinecap="round" />

      {/* ── Battery ── */}
      {/* Outer casing */}
      <rect x={batX - 30} y={batY - 70} width={60} height={140} rx={12}
        fill="#334155" stroke="#1e293b" strokeWidth={2.5} />
      {/* + terminal */}
      <rect x={batX - 8} y={batY - 86} width={16} height={18} rx={4} fill="#e2e8f0" />
      <text x={batX} y={batY - 73} textAnchor="middle"
        fill="#f8fafc" fontSize={13} fontWeight={900} fontFamily="Inter,sans-serif">+</text>
      {/* − terminal */}
      <rect x={batX - 8} y={batY + 68} width={16} height={18} rx={4} fill="#e2e8f0" />
      <text x={batX} y={batY + 82} textAnchor="middle"
        fill="#f8fafc" fontSize={13} fontWeight={900} fontFamily="Inter,sans-serif">−</text>
      {/* Charge stripe */}
      <rect x={batX - 20} y={batY - 50} width={40} height={80} rx={6} fill="#22c55e" opacity={0.9} />
      <rect x={batX - 20} y={batY + 10} width={40} height={20} rx={0} fill="#16a34a" opacity={0.5} />
      {/* BATTERY label */}
      <text x={batX} y={batY + 6} textAnchor="middle"
        fill="#f8fafc" fontSize={12} fontWeight={800} fontFamily="Inter,sans-serif" letterSpacing={0.5}>
        BATTERY
      </text>

      {/* ── Light Bulb ── */}
      {/* Glow halo (scales with bulbGlow) */}
      <circle cx={bulbX} cy={bulbY} r={55 * rayScale}
        fill={accentColor} opacity={clamp(bulbGlow * 0.22, 0, 0.22)} />
      <circle cx={bulbX} cy={bulbY} r={42 * rayScale}
        fill={accentColor} opacity={clamp(bulbGlow * 0.22, 0, 0.22)} />
      {/* Rays (8 lines emanating from bulb) */}
      {Array.from({ length: 8 }, (_, k) => {
        const a = (k / 8) * Math.PI * 2;
        const r1 = 38 * rayScale;
        const r2 = 52 * rayScale;
        return (
          <line key={k}
            x1={bulbX + r1 * Math.cos(a)} y1={bulbY + r1 * Math.sin(a)}
            x2={bulbX + r2 * Math.cos(a)} y2={bulbY + r2 * Math.sin(a)}
            stroke={accentColor}
            strokeWidth={3.5}
            strokeLinecap="round"
            opacity={clamp(bulbGlow * 0.85, 0, 1)}
          />
        );
      })}
      {/* Bulb glass (dome) */}
      <path
        d={`M${bulbX - 28},${bulbY + 20} Q${bulbX - 30},${bulbY - 42} ${bulbX},${bulbY - 44} Q${bulbX + 30},${bulbY - 42} ${bulbX + 28},${bulbY + 20} Z`}
        fill={bulbYellow}
        stroke="#b45309" strokeWidth={2.5}
      />
      {/* Filament glow inside */}
      <path
        d={`M${bulbX - 10},${bulbY + 10} Q${bulbX},${bulbY - 10} ${bulbX + 10},${bulbY + 10}`}
        fill="none"
        stroke="#fff"
        strokeWidth={3}
        strokeLinecap="round"
        opacity={clamp(bulbGlow, 0, 1)}
      />
      {/* Bulb base (screw cap) */}
      <rect x={bulbX - 18} y={bulbY + 20} width={36} height={28} rx={6}
        fill="#92400e" stroke="#78350f" strokeWidth={2} />
      {[0, 8, 16, 24].map(dy => (
        <line key={dy} x1={bulbX - 18} y1={bulbY + 22 + dy} x2={bulbX + 18} y2={bulbY + 22 + dy}
          stroke="#78350f" strokeWidth={1.5} opacity={0.5} />
      ))}
      {/* BULB label */}
      <text x={bulbX} y={bulbY + 66} textAnchor="middle"
        fill="#78350f" fontSize={13} fontWeight={800} fontFamily="Inter,sans-serif">
        Bulb
      </text>

      {/* ── Switch ── */}
      {/* Switch base */}
      <rect x={swX - 54} y={swY - 14} width={108} height={28} rx={14}
        fill="#e2e8f0" stroke="#94a3b8" strokeWidth={2.5} />
      {/* Lever (always closed — current flows) */}
      <g transform={`translate(${swX} ${swY}) rotate(${switchWobble})`}>
        <line x1={-30} y1={0} x2={30} y2={0}
          stroke="#0f172a" strokeWidth={8} strokeLinecap="round" />
        <circle cx={-30} cy={0} r={6} fill="#64748b" />
        <circle cx={30}  cy={0} r={6} fill="#64748b" />
      </g>
      {/* SWITCH label */}
      <text x={swX} y={swY + 30} textAnchor="middle"
        fill="#475569" fontSize={13} fontWeight={700} fontFamily="Inter,sans-serif">
        Switch (ON)
      </text>

      {/* ── Electron dots on the wire ── */}
      {dotElements}

      {/* ── Caption ── */}
      <g transform="translate(400 424)">
        <rect x={-280} y={-20} width={560} height={36} rx={18}
          fill="rgba(255,255,255,0.93)" stroke="#fde68a" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle"
          fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};
